import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, ExternalLink, Gift, MapPin, RefreshCw, SlidersHorizontal } from 'lucide-react';
import { api } from '../../services/apiService';
import { Card, CardHeader, Chip, ConfirmDialog, Empty, Note, LOCATION_STATUS_TONE } from './financeUi';
import ChangePriceEditor from './ChangePriceEditor';
import PaymentMethodPanel from './PaymentMethodPanel';
import {
  billingApi, describePaymentMethod, paymentMethodButtonLabel, type PaymentMethodsResult,
} from '../../services/billingApi';
import {
  money, nextInvoice, locationMonthlyCents, isBillingLocation,
  type LocationRow, type SubscriptionLike,
} from './financeFormat';
import type { DeviceUnitRow } from '../../services/hardwareApi';
import type { PricePlan } from '../../types';
import { RubyLoader } from '../RubyLoader';

import { RubyGemMark } from '../RubyGemMark';
/**
 * FINANCE → LOCATIONS
 *
 * Spec §9: one row per location — plan line · packages · device lines · card
 * used · next invoice · status · make free / change price.
 *
 * This is the SUMMARY, not a replacement: the per-location billing page at
 * `/tenant/{id}/location/{requestId}?tab=billing` keeps working and every row
 * links straight to it. Operators have that page bookmarked; retiring it is a
 * separate decision that has not been taken.
 */

/** Statuses where no location (and no subscription) exists yet — mirrors the location page. */
const PRE_APPROVAL = new Set(['pending', 'info_requested', 'rejected']);

/** make-free sets the status back to `active`, so on these it also revives the store. */
const REVIVED_BY_MAKE_FREE = new Set(['inactive', 'grace', 'deactivation_requested', 'payment_pending']);

interface Props {
  tenantId: string;
  locations: LocationRow[];
  loading: boolean;
  units: DeviceUnitRow[];
  modelNames: Record<string, string>;
  prices: PricePlan[];
  /** Per-store package SKUs from the billing catalog — the same lines apply to every store. */
  perStoreSkus: any[];
  /** The whole billing catalog — the change-price editor prices packages from it. */
  skus: any[];
  onReloadLocations: () => Promise<void> | void;
  notify: (message: string) => void;
}

/**
 * **Change price** now opens the full editor (packages, discounts, published
 * price) rather than a one-field dialog, and holds its own state — so the only
 * thing left on this union is make-free. The base-plan price picker the old
 * `price` action was lives on inside `ChangePriceEditor`, unchanged, so nothing
 * an operator could do before has been taken away.
 */
type Action = { kind: 'free'; row: LocationRow } | null;

const LocationsPanel: React.FC<Props> = ({
  tenantId, locations, loading, units, modelNames, prices, perStoreSkus, skus,
  onReloadLocations, notify,
}) => {
  const navigate = useNavigate();
  const [subs, setSubs] = useState<Record<string, SubscriptionLike | null>>({});
  const [subsLoading, setSubsLoading] = useState(false);
  const [action, setAction] = useState<Action>(null);
  const [busy, setBusy] = useState(false);
  /**
   * The change-price editor is held open by `requestId`, NEVER by the row
   * object.
   *
   * Publishing a plan reloads the tenant's locations, which replaces every row
   * in `locations` with a fresh object. An editor holding the old row would
   * carry on showing pre-change values, and one keyed on object identity would
   * unmount mid-edit. Keeping the id and re-looking-up the row each render means
   * a reload refreshes the editor instead of destroying it — and an editor whose
   * location has genuinely disappeared closes itself rather than rendering a
   * store that is no longer there.
   */
  const [editing, setEditing] = useState<string | null>(null);
  const editingRow = useMemo(
    () => (editing ? locations.find((l) => l.requestId === editing) ?? null : null),
    [editing, locations],
  );

  /**
   * PAYMENT METHODS — customer-level, so they are held HERE and read once,
   * not per row.
   *
   * The panel is a separate component but it does not own this state: the
   * summary on the header button and the brand/last four in the Card column
   * both come from it, and two independent fetches would let those two places
   * disagree about which card is being charged.
   *
   * `paymentMethodsOpen` is a plain boolean, never a row object — a locations
   * reload replaces every row in `locations`, and an open dialog keyed on one
   * would be torn down mid-flow.
   */
  const [paymentMethodsOpen, setPaymentMethodsOpen] = useState(false);
  const [methods, setMethods] = useState<PaymentMethodsResult | null>(null);
  const [methodsLoading, setMethodsLoading] = useState(true);
  const [methodsError, setMethodsError] = useState<string | null>(null);

  /**
   * Reloads the PAYMENT METHODS ONLY.
   *
   * Deliberately NOT `onReloadLocations`. Changing a card changes nothing about
   * the locations table, and reusing the locations reload here would replace
   * every row object underneath an open change-price editor for no reason.
   */
  const loadMethods = useCallback(async () => {
    setMethodsLoading(true);
    try {
      const res = await billingApi.listPaymentMethods(tenantId);
      setMethods(res);
      // A transport failure and a `readable: false` answer are different facts
      // and are surfaced differently: this is only for the former.
      setMethodsError(null);
    } catch (err: any) {
      setMethods(null);
      setMethodsError(err?.message || 'The payment methods on file could not be read.');
    } finally {
      setMethodsLoading(false);
    }
  }, [tenantId]);
  useEffect(() => { loadMethods(); }, [loadMethods]);

  /**
   * The method Stripe would actually charge, or null when there is not one to
   * name.
   *
   * Null has TWO meanings and the button below must not merge them: nothing is
   * on file, or things are on file and none of them is the customer default.
   * `paymentMethodButtonLabel` is what tells them apart — see its tests.
   */
  const defaultMethod = useMemo(() => {
    const list = methods?.paymentMethods ?? [];
    // `[] || fallback` returns `[]` — an empty array is truthy. Length, always.
    if (list.length === 0) return null;
    return list.find((m) => m.isDefault) ?? null;
  }, [methods]);

  /** The header button's label and tooltip, decided in one tested place. */
  const methodButton = useMemo(
    () => paymentMethodButtonLabel({ loading: methodsLoading, transportError: !!methodsError, data: methods }),
    [methodsLoading, methodsError, methods],
  );

  const subscriptionIds = useMemo(
    () => locations.map((l) => l.stripeSubscriptionId).filter((id): id is string => !!id),
    [locations],
  );

  /**
   * Next-invoice dates come from Stripe itself, one read per location
   * subscription. They are NOT copied from the tenant's own renewal date: a
   * location added mid-month bills on its own anchor, and showing the tenant's
   * date in a column headed "Next invoice" would be wrong for exactly the stores
   * an operator is most likely to be asking about.
   */
  const loadSubs = async () => {
    if (subscriptionIds.length === 0) { setSubs({}); return; }
    setSubsLoading(true);
    const results = await Promise.allSettled(
      subscriptionIds.map((id) => api.superAdmin.getSubscription(id)),
    );
    const next: Record<string, SubscriptionLike | null> = {};
    results.forEach((r, i) => { next[subscriptionIds[i]] = r.status === 'fulfilled' ? (r.value as any) : null; });
    setSubs(next);
    setSubsLoading(false);
  };
  useEffect(() => { loadSubs(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [subscriptionIds.join(',')]);

  const unitsByLocation = useMemo(() => {
    const map: Record<string, DeviceUnitRow[]> = {};
    for (const u of units) {
      const key = u.locationId || '';
      if (!key) continue;
      (map[key] ||= []).push(u);
    }
    return map;
  }, [units]);

  const deviceLine = (row: LocationRow): string => {
    const list = [
      ...(unitsByLocation[row.locationId || ''] || []),
      ...(row.locationId ? [] : unitsByLocation[row.requestId] || []),
    ];
    if (list.length === 0) return '—';
    return list
      .slice(0, 3)
      .map((u) => {
        const name = modelNames[u.modelId] || u.modelId;
        const term = u.agreement?.termMonths ? ` · ${u.agreement.termMonths}mo` : '';
        return `${name}${term}`;
      })
      .join(' · ') + (list.length > 3 ? ` +${list.length - 3}` : '');
  };

  /**
   * Which card pays this subscription.
   *
   * Stripe attaches a PaymentMethod to a CUSTOMER, and this platform keeps one
   * Customer per tenant, so unless a subscription carries its own override every
   * location is charged to the tenant's default card. The id is not expanded
   * here, so the brand and last four are deliberately NOT shown: inventing
   * "Visa ···· 4242" from a `pm_…` id would be a fabrication.
   */
  const cardLabel = (row: LocationRow): { text: string; title?: string } => {
    if (row.isFree) return { text: 'not charged' };
    if (!row.stripeSubscriptionId) return { text: '—' };
    const sub = subs[row.stripeSubscriptionId];
    if (sub === undefined) return { text: subsLoading ? '…' : '—' };
    if (sub === null) return { text: 'unread', title: 'Stripe could not be read for this subscription.' };
    const pm: any = (sub as any).default_payment_method;
    if (pm && typeof pm === 'object' && pm.card) {
      return { text: `${String(pm.card.brand || 'card').toUpperCase()} ···· ${pm.card.last4}` };
    }
    if (typeof pm === 'string' && pm) {
      // The id is NOT expanded on this read, so the brand and last four are
      // looked up in the methods we already have rather than invented. A method
      // we cannot find is reported as an override, not guessed at.
      const known = (methods?.paymentMethods ?? []).find((m) => m.id === pm);
      if (known) {
        return {
          text: describePaymentMethod(known).label,
          title: 'This subscription pins its own payment method, overriding the customer default.',
        };
      }
      return { text: 'own card', title: `This subscription overrides the customer default (${pm}).` };
    }
    // No override: the tenant's Customer default pays this store. Naming it is
    // only possible when that default was actually read — otherwise the old,
    // honest wording stands rather than a blank instrument dressed up as real.
    if (defaultMethod) {
      return {
        text: describePaymentMethod(defaultMethod).label,
        title: "The tenant's default payment method, which charges every location.",
      };
    }
    return { text: 'tenant default', title: "Charged to the tenant's default payment method on their Stripe Customer." };
  };

  const openBilling = (row: LocationRow) =>
    navigate(`/tenant/${tenantId}/location/${row.requestId}?tab=billing`);

  const blockReason = (row: LocationRow): string | null => {
    if (PRE_APPROVAL.has(row.status || '')) {
      return 'This location is still awaiting a decision. Approve or reject it from its own page first — that is where a pending request\'s price is set.';
    }
    if (row.isFree) return 'This location is already free.';
    return null;
  };

  const runAction = async () => {
    if (!action) return;
    const { row } = action;
    setBusy(true);
    try {
      await api.locationBilling.makeFreeRequest(row.requestId, { tenantId });
      notify(`${row.name || 'Location'} is now free — its subscription was cancelled.`);
      setAction(null);
      await onReloadLocations();
    } catch (err: any) {
      notify(err?.message || 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  const perStoreLabel = perStoreSkus.length
    ? perStoreSkus.map((s: any) => s.name).join(', ')
    : null;

  return (
    <div className="space-y-4 text-stone-800">
      <Card>
        <CardHeader
          icon={<MapPin size={15} />}
          title="Locations — one subscription each"
          hint={`${locations.filter(isBillingLocation).length} billing`}
          actions={
            <>
              {/*
                Beside "Change price", but on the HEADER rather than on a row —
                a payment method belongs to the tenant's one Stripe Customer and
                charges every location, so a per-row button would state the
                opposite of the truth. The label names the method actually being
                charged when there IS one, and otherwise says which of the other
                four things is true — unread, no Stripe customer, on file but
                none selected, or genuinely nothing — so an operator can answer
                "how do they pay us" without opening anything.
              */}
              <button
                onClick={() => setPaymentMethodsOpen(true)}
                title={methodButton.title}
                className="px-2.5 py-1.5 rounded-lg border border-stone-300 text-[11px] font-semibold text-stone-700 hover:bg-stone-100 inline-flex items-center gap-1.5 min-h-[36px]"
              >
                <CreditCard size={12} />
                {methodButton.label}
              </button>
              <button
                onClick={() => { onReloadLocations(); loadSubs(); }}
                disabled={loading || subsLoading}
                className="text-xs text-stone-500 hover:text-stone-900 inline-flex items-center gap-1.5 min-h-[36px] px-2"
              >
                {loading || subsLoading ? <RubyGemMark size={13} drawing color="currentColor" /> : <RefreshCw size={13} />} refresh
              </button>
            </>
          }
        />

        {loading && locations.length === 0 ? (
          <RubyLoader variant="section" className="py-10" />
        ) : locations.length === 0 ? (
          <Empty
            icon={<MapPin size={34} />}
            title="No locations on this tenant"
            text="Locations are requested by the tenant from their own app and approved here. Nothing to bill until one exists."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-stone-800">
              <thead className="bg-stone-50 border-b border-stone-200 text-[11px] uppercase tracking-wide text-stone-500 text-left">
                <tr>
                  <th className="px-3 py-2 font-semibold">Location</th>
                  <th className="px-3 py-2 font-semibold">Plan</th>
                  <th className="px-3 py-2 font-semibold">Devices</th>
                  <th className="px-3 py-2 font-semibold">Card</th>
                  <th className="px-3 py-2 font-semibold text-right">Monthly</th>
                  <th className="px-3 py-2 font-semibold">Next invoice</th>
                  <th className="px-3 py-2 font-semibold">Status</th>
                  <th className="px-3 py-2 font-semibold text-right">Billing</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {locations.map((row) => {
                  const sub = row.stripeSubscriptionId ? subs[row.stripeSubscriptionId] : null;
                  const invoice = nextInvoice(sub);
                  const card = cardLabel(row);
                  const status = row.status || 'active';
                  return (
                    <tr key={row.requestId} className="hover:bg-stone-50 align-top">
                      <td className="px-3 py-3">
                        <div className="font-semibold text-stone-900">{row.name || row.requestId}</div>
                        <div className="text-[11px] text-stone-500 font-mono truncate max-w-[14rem]">
                          {row.stripeSubscriptionId || 'no subscription'}
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        {row.isFree
                          ? <Chip tone="emerald">Free</Chip>
                          : <span className="text-[11px] text-stone-600">
                            {row.priceName || row.priceId || '—'}
                          </span>}
                        {perStoreLabel && !row.isFree && (
                          <div className="text-[11px] text-stone-400 mt-0.5">+ {perStoreLabel}</div>
                        )}
                      </td>
                      <td className="px-3 py-3 text-[11px] text-stone-600">{deviceLine(row)}</td>
                      <td className="px-3 py-3 text-[11px] text-stone-600" title={card.title}>{card.text}</td>
                      <td className="px-3 py-3 text-right tabular-nums font-medium text-stone-900">
                        {money(locationMonthlyCents(row))}
                      </td>
                      <td className={`px-3 py-3 text-[11px] ${invoice.final ? 'text-amber-700' : 'text-stone-600'}`}>
                        {invoice.label}
                      </td>
                      <td className="px-3 py-3">
                        <Chip tone={LOCATION_STATUS_TONE[status] || 'stone'}>{status.replace(/_/g, ' ')}</Chip>
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center justify-end gap-1.5 flex-wrap">
                          {!PRE_APPROVAL.has(status) && !row.isFree && (
                            <button
                              onClick={() => setAction({ kind: 'free', row })}
                              className="px-2.5 py-1.5 rounded-lg border border-emerald-300 bg-emerald-50 text-[11px] font-semibold text-emerald-800 hover:bg-emerald-100"
                            >
                              Make free
                            </button>
                          )}
                          {!PRE_APPROVAL.has(status) && (
                            <button
                              onClick={() => setEditing(row.requestId)}
                              title="Packages, discounts and the price this client actually pays at this store"
                              className="px-2.5 py-1.5 rounded-lg border border-stone-300 text-[11px] font-semibold text-stone-700 hover:bg-stone-100 inline-flex items-center gap-1"
                            >
                              <SlidersHorizontal size={11} /> Change price
                            </button>
                          )}
                          <button
                            onClick={() => openBilling(row)}
                            className="px-2.5 py-1.5 rounded-lg bg-stone-900 text-white text-[11px] font-semibold hover:bg-black inline-flex items-center gap-1"
                            title="Open this location's own billing page"
                          >
                            Open <ExternalLink size={11} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="px-4 py-3 border-t border-stone-200 space-y-2">
          <Note>
            <strong>This is the summary.</strong> Each row's <em>Open</em> button goes to that location's own billing
            page — approve, reject, comp, transfer, deactivate and its documents all still live there, unchanged.
          </Note>
          <p className="text-[11px] text-stone-500 leading-relaxed">
            Stripe attaches a payment method to a <em>Customer</em>, and this platform keeps one Customer per tenant, so
            unless a subscription carries its own override every location is charged to the tenant's default card.
            Locations that are pending, rejected or inactive have no subscription and are shown at {money(0)}.
          </p>
        </div>
      </Card>

      {/* Make free ------------------------------------------------------------ */}
      <ConfirmDialog
        open={action?.kind === 'free'}
        title={`Make ${action?.row.name || 'this location'} free?`}
        confirmLabel="Make free"
        busy={busy}
        blocked={action ? blockReason(action.row) : null}
        onConfirm={runAction}
        onClose={() => setAction(null)}
      >
        <p className="flex items-start gap-2">
          <Gift size={16} className="text-emerald-600 mt-0.5 shrink-0" />
          <span>
            The location stays active and its Stripe subscription is <strong>cancelled</strong>, so the tenant stops
            being billed {action ? money(locationMonthlyCents(action.row)) : ''} a month for it. The stored price is
            cleared — putting it back on a plan later means picking a price again.
          </span>
        </p>
        {action && REVIVED_BY_MAKE_FREE.has(action.row.status || '') && (
          <Note tone="warn">
            This location is <strong>{(action.row.status || '').replace(/_/g, ' ')}</strong> today. Making it free also
            sets it back to <strong>active</strong> — the tenant gets the store back, at no charge.
          </Note>
        )}
      </ConfirmDialog>

      {/* Change price ---------------------------------------------------------
          The full editor: this store's packages, what the client actually pays
          for each, the discounts already applied, and the base plan line beside
          them. Publishing supersedes the plan assignment, updates the
          subscription immediately with proration, and emails both sides what
          changed — all from billing-service's activation, never from here.

          Rendered from `editingRow`, which is re-looked-up out of `locations` on
          every render, so the reload a publish triggers refreshes the editor
          rather than tearing it down. */}
      {/* Payment method --------------------------------------------------------
          Customer-level, so it is opened from the header and not from a row.
          `onReload` reloads ONLY the payment methods — never the locations,
          which a card change does not affect and whose reload would replace
          every row object beneath an open change-price editor. */}
      <PaymentMethodPanel
        open={paymentMethodsOpen}
        tenantId={tenantId}
        data={methods}
        loading={methodsLoading}
        error={methodsError}
        onReload={loadMethods}
        onClose={() => setPaymentMethodsOpen(false)}
        notify={notify}
      />

      {editingRow && (
        <ChangePriceEditor
          tenantId={tenantId}
          row={editingRow}
          skus={skus}
          locations={locations}
          prices={prices}
          onClose={() => setEditing(null)}
          onReloadLocations={onReloadLocations}
          notify={notify}
        />
      )}
    </div>
  );
};

export default LocationsPanel;
